import React, { useState } from 'react'

import {
  InnerWidgetPatchBody,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'

import Switch from 'components_new/atoms/Switch'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { useTranslation } from 'language/translator'

interface TitleSectionProps {
  onClose: () => void
  settings: TWidgetSettings
  updateWidget: (body: InnerWidgetPatchBody) => void
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.BUBBLE_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.FUNNEL,
  WidgetType.KEY_FIGURE,
  WidgetType.LINE_CHART,
  WidgetType.LIST,
  WidgetType.MAP,
  WidgetType.PIE_CHART
]

const TitleSection = (props: TitleSectionProps) => {
  const { onClose, settings, updateWidget } = props
  const { t } = useTranslation()

  const [drawer, setDrawer] = useState<{
    open: boolean
    title: string
    data: {
      property: 'title' | 'subtitle'
      text: string
      visible: boolean
    }
  }>({
    open: false,
    title: '',
    data: { property: 'title', text: '', visible: true }
  })
  const [dialog, setDialog] = useState<{
    open: boolean
    title: string
    data: {
      property: 'title' | 'subtitle' | 'tooltip'
      value: string
    }
  }>({
    open: false,
    title: '',
    data: { property: 'title', value: '' }
  })

  if (!LEGAL_TYPES.includes(settings.type.selected)) {
    return null
  }

  /*-- drawer --*/
  const handleBackDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, open: false }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleOpenDrawer = (property: 'subtitle' | 'title') => {
    const drawerTitle = t('global', property === 'title' ? 'title' : 'subtitle')
    const text = property === 'title' ? settings.title : settings.subtitle
    const visible =
      property === 'title'
        ? settings.show_title
        : settings.show_subtitle

    setDrawer({
      open: true,
      title: drawerTitle,
      data: { property: property, text: text, visible: visible }
    })
  }

  /*-- toggle --*/
  const handleToggle = (property: 'subtitle' | 'title') => {
    if (property === 'title') {
      updateWidget({
        show_title: !settings.show_title
      })
    } else {
      updateWidget({
        show_subtitle: !settings.show_subtitle
      })
    }
  }

  /*-- dialog --*/
  const handleOpenDialog = (property: 'subtitle' | 'title' | 'tooltip') => {
    let dialogTitle = t('global', 'title')
    let text = settings.title

    if (property === 'subtitle') {
      dialogTitle = t('global', 'subtitle')
      text = settings.subtitle
    } else if (property === 'tooltip') {
      dialogTitle = t('global', 'help_text')
      text = settings.tooltip
    }

    setDialog({
      open: true,
      title: dialogTitle,
      data: { property: property, value: text }
    })
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    updateWidget({ [dialog.data.property]: dialog.data.value })
    handleCloseDialog()
  }

  return (
    <>
      <SettingsGroup title={t('global', 'title')}>
        <SettingsItem
          onClick={() => handleOpenDrawer('title')}
          title={t('global', 'title')}
          value={settings.title}
        />
        <SettingsItem
          onClick={() => handleOpenDrawer('subtitle')}
          title={t('global', 'subtitle')}
          value={settings.subtitle}
        />
      </SettingsGroup>

      {/*-- drawer --*/}
      <SettingsDrawer
        open={drawer.open}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={drawer.title}
      >
        <SettingsGroup>
          <SettingsItem
            actions={
              <Switch
                checked={
                  drawer.data.property === 'title'
                    ? settings.show_title
                    : settings.show_subtitle
                }
                onChange={() => handleToggle(drawer.data.property)}
                size="small"
              />
            }
            title={t('global', 'show')}
          />
          <SettingsItem
            onClick={() => handleOpenDialog(drawer.data.property)}
            title={t('global', 'text')}
            value={
              drawer.data.property === 'title'
                ? settings.title
                : settings.subtitle
            }
            variant="edit"
          />
        </SettingsGroup>

        {drawer.data.property === 'title' ? (
          <SettingsGroup>
            <SettingsItem
              onClick={() => handleOpenDialog('tooltip')}
              title={t('global', 'help_text')}
              value={settings.tooltip}
              variant="edit"
            />
          </SettingsGroup>
        ) : null}
      </SettingsDrawer>

      {/*-- dialog --*/}
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        title={dialog.title}
        type={'text'}
        value={dialog.data.value}
      />
    </>
  )
}

export default TitleSection
